import { useCurrentUserRoles } from '@data-delivery/permission-ui-data-access-state';
import { type ReactNode, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { hasPermission } from '../utils/abac';
import { PATHS } from '../utils/routing';
import { FallbackLoader } from './top-progress-bar';

type ResourceType =
  | 'businessGroups'
  | 'products'
  | 'assignments'
  | 'users'
  | 'directoryPermissions'
  | 'contentAccessRoles'
  | 'landingFolderRestrictions'
  | 'operatorUsers'
  | 'operatorGroups'
  | 'retentions';

type PermissionAction = 'view' | 'create' | 'update' | 'delete';

interface ProtectedRouteProps {
  children: ReactNode;
  resource: ResourceType;
  action?: PermissionAction;
  fallbackPath?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  resource,
  action = 'view',
  fallbackPath = PATHS.notAuthorized,
}) => {
  const { data: userRoles, isLoading, isError } = useCurrentUserRoles();

  if (isLoading) {
    return <FallbackLoader />;
  }

  if (isError || !userRoles || userRoles.length === 0) {
    return <Navigate to={fallbackPath} replace />;
  }

  const hasAccess = hasPermission(userRoles, resource, action);

  if (!hasAccess) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <Suspense fallback={<FallbackLoader />}>{children}</Suspense>;
};
